import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, StatusBadge } from "@/components/DataTable";
import { Plus, Award } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const CERT_TYPES = [
  { key: "admission", label: "Admission Certificate" },
  { key: "bonafide", label: "Bonafide Certificate" },
  { key: "hall_ticket", label: "Hall Ticket" },
  { key: "completion", label: "Completion Certificate" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function StudentsPage() {
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ name:"", email:"", phone:"", parent_name:"", parent_phone:"", batch_id:"", gender:"M" });

  const load = async () => {
    const [{ data: s }, { data: b }] = await Promise.all([api.get("/students"), api.get("/batches")]);
    setStudents(s); setBatches(b);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const batch = batches.find(b => b.id === form.batch_id);
    const rollNo = `CE${Date.now()}`.slice(-9);
    await api.post("/students", {
      ...form,
      roll_no: rollNo,
      batch_name: batch?.name, course_id: batch?.course_id, course_name: batch?.course_name,
      admission_date: new Date().toISOString().slice(0,10),
      status: "active",
    });
    setOpen(false);
    setForm({ name:"", email:"", phone:"", parent_name:"", parent_phone:"", batch_id:"", gender:"M" });
    toast.success("Student added");
    load();
  };

  const filtered = students.filter(s => !q || [s.name, s.roll_no, s.email, s.batch_name].some(x => (x||"").toLowerCase().includes(q.toLowerCase())));

  return (
    <div>
      <PageHeader
        title="Students"
        subtitle={`${students.length} enrolled learners`}
        actions={
          <div className="flex items-center gap-2">
            <input placeholder="Search…" data-testid="students-search"
              value={q} onChange={e=>setQ(e.target.value)}
              className="px-3 py-2 border border-[#E0DFDC] rounded-md text-sm bg-white"/>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button className="btn-primary flex items-center gap-2" data-testid="add-student-btn"><Plus size={16}/> Add student</button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New admission</DialogTitle></DialogHeader>
                <form onSubmit={create} className="space-y-3">
                  <input required placeholder="Full name" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" data-testid="student-name-input" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Email" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" data-testid="student-email-input" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/>
                    <input placeholder="Phone" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" data-testid="student-phone-input" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Parent name" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" data-testid="student-parent-name-input" value={form.parent_name} onChange={e=>setForm({...form, parent_name:e.target.value})}/>
                    <input placeholder="Parent phone" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" data-testid="student-parent-phone-input" value={form.parent_phone} onChange={e=>setForm({...form, parent_phone:e.target.value})}/>
                  </div>
                  <select className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" data-testid="student-batch-select" value={form.batch_id} onChange={e=>setForm({...form, batch_id:e.target.value})} required>
                    <option value="">Select batch</option>
                    {batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                  <DialogFooter><button type="submit" className="btn-primary" data-testid="student-save-btn">Save admission</button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <DataTable
        testid="students-table"
        columns={[
          { key: "roll_no", label: "Roll No" },
          { key: "name", label: "Name", render: r => (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#EAF3FB] text-[#0A66C2] flex items-center justify-center text-xs font-semibold">{r.name?.[0]}</div>
              <div>
                <div className="font-semibold">{r.name}</div>
                <div className="text-xs text-[#00000099]">{r.email}</div>
              </div>
            </div>
          )},
          { key: "batch_name", label: "Batch" },
          { key: "course_name", label: "Course" },
          { key: "phone", label: "Phone" },
          { key: "status", label: "Status", render: r => <StatusBadge status={r.status}/> },
          { key: "certificates", label: "Certificates", render: r => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-xs px-3 py-1 rounded-full bg-[#EAF3FB] text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white font-semibold inline-flex items-center gap-1" data-testid={`cert-menu-${r.id}`}>
                  <Award size={12}/> Issue
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {CERT_TYPES.map(c => (
                  <DropdownMenuItem key={c.key} data-testid={`cert-${c.key}-${r.id}`} onClick={async () => {
                    const token = localStorage.getItem("access_token");
                    // Open with Bearer via fetch, then window.open blob URL
                    try {
                      const res = await fetch(`${BACKEND}/api/certificates/generate/${c.key}/${r.id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                        credentials: "include",
                      });
                      if (!res.ok) throw new Error("Failed");
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      window.open(url, "_blank");
                      toast.success(`${c.label} generated`);
                    } catch (e) {
                      toast.error("Failed to generate certificate");
                    }
                  }}>
                    {c.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )},
        ]}
        rows={filtered}
      />
    </div>
  );
}
